// src/components/common/MoreMenu.jsx
//
// Menú "⋯" de acciones secundarias (ficha del reclamo, filas de ajustadores).
// Botón con aria-expanded + lista; se cierra con Escape (y devuelve el foco al
// botón), con clic fuera y al elegir una opción. Reemplaza a los <details> que
// quedaban abiertos. Las opciones son { label, icon, onSelect, danger }.
import React, { useEffect, useRef, useState } from 'react';

const MoreMenu = ({ label, items, className = '' }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = event => { if (!rootRef.current?.contains(event.target)) setOpen(false); };
    const onKey = event => {
      if (event.key === 'Escape') { setOpen(false); buttonRef.current?.focus(); }
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    // Al abrir, el foco pasa a la primera opción.
    rootRef.current?.querySelector('[role="menuitem"]')?.focus();
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const onMenuKeyDown = event => {
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const options = [...rootRef.current.querySelectorAll('[role="menuitem"]')];
    const index = options.indexOf(document.activeElement);
    let next;
    if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = options.length - 1;
    else next = event.key === 'ArrowDown' ? (index + 1) % options.length : (index - 1 + options.length) % options.length;
    options[next]?.focus();
  };

  // La lista evita el mousedown por defecto para no mover el foco: en Safari/Firefox de Mac los
  // botones no lo reciben al pulsarse y el cierre por blur se llevaría el menú antes del clic.
  return (
    <div className={`more-menu ${className}`} ref={rootRef}
      // Tab hacia fuera del menú lo cierra (antes quedaba abierto detrás del foco).
      onBlur={event => { if (open && !rootRef.current?.contains(event.relatedTarget)) setOpen(false); }}>
      <button ref={buttonRef} type="button" className="more-menu-button" aria-haspopup="menu" aria-expanded={open} aria-label={label} title={label} onClick={() => setOpen(value => !value)}>
        <i className="fa-solid fa-ellipsis" aria-hidden="true"></i>
      </button>
      {open && (
        <div className="more-menu-list" role="menu" aria-label={label} onKeyDown={onMenuKeyDown} onMouseDown={event => event.preventDefault()}>
          {items.filter(Boolean).map(item => (
            <button key={item.label} type="button" role="menuitem" className={item.danger ? 'is-danger' : ''}
              onClick={() => { setOpen(false); item.onSelect(); buttonRef.current?.focus(); }}>
              {item.icon && <i className={`fa-solid ${item.icon}`} aria-hidden="true"></i>} {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default MoreMenu;
